import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../../../services/api';

const SalesTargetsTab = () => {
  const [salesTargets, setSalesTargets] = useState([]);
  const [salesTargetsLoading, setSalesTargetsLoading] = useState(false);
  const [editingSalesTarget, setEditingSalesTarget] = useState(null);
  const [locations, setLocations] = useState([]);
  const [newSalesTarget, setNewSalesTarget] = useState({
    location_id: '', year: new Date().getFullYear(), month: new Date().getMonth() + 1, target_amount: ''
  });
  const [salesTargetFilter, setSalesTargetFilter] = useState({ year: null, month: null, location_id: null });

  useEffect(() => {
    loadLocations();
    loadSalesTargets();
  }, []);

  const loadLocations = async () => {
    try {
      const { data } = await api.get('/locations/');
      const locs = Array.isArray(data.data) ? data.data : (data.data?.locations || data.locations || []);
      const mappedLocations = locs.map(loc => ({
        ...loc,
        name: loc.name || loc.nazwa
      }));
      setLocations(mappedLocations);
    } catch (err) {
      console.error('Blad podczas pobierania lokalizacji:', err);
    }
  };

  const loadSalesTargets = async (filters = null) => {
    setSalesTargetsLoading(true);
    try {
      const activeFilters = filters || salesTargetFilter;
      const params = {};
      if (activeFilters.year) params.year = activeFilters.year;
      if (activeFilters.month) params.month = activeFilters.month;
      if (activeFilters.location_id) params.location_id = activeFilters.location_id;

      const { data } = await api.get('/admin/sales-targets', { params });
      if (data.success) {
        setSalesTargets(data.data?.targets || []);
      }
    } catch (err) {
      console.error('Blad ladowania celow sprzedazy:', err);
    } finally {
      setSalesTargetsLoading(false);
    }
  };

  const saveSalesTarget = async () => {
    if (!newSalesTarget.location_id || !newSalesTarget.target_amount) {
      toast.error('Wybierz lokalizację i podaj kwotę celu');
      return;
    }
    try {
      const payload = {
        location_id: parseInt(newSalesTarget.location_id),
        year: parseInt(newSalesTarget.year) || new Date().getFullYear(),
        month: parseInt(newSalesTarget.month) || (new Date().getMonth() + 1),
        target_amount: parseFloat(newSalesTarget.target_amount)
      };

      if (editingSalesTarget && editingSalesTarget.id) {
        payload.id = editingSalesTarget.id;
      }

      const { data } = await api.post('/admin/sales-targets', payload);
      if (data.success) {
        toast.success(editingSalesTarget ? 'Cel sprzedaży zaktualizowany' : 'Cel sprzedaży zapisany');
        setEditingSalesTarget(null);
        setNewSalesTarget({ location_id: '', year: new Date().getFullYear(), month: new Date().getMonth() + 1, target_amount: '' });
        loadSalesTargets();
      } else {
        toast.error(data.message || 'Błąd zapisywania celu');
      }
    } catch (err) {
      console.error('Blad zapisywania celu:', err);
      toast.error('Błąd połączenia');
    }
  };

  const deleteSalesTarget = async (targetId) => {
    if (!window.confirm('Czy na pewno usunąć ten cel?')) return;
    try {
      const { data } = await api.delete(`/admin/sales-targets/${targetId}`);
      if (data.success) {
        toast.success('Cel usunięty');
        loadSalesTargets();
      } else {
        toast.error(data.message || 'Błąd usuwania');
      }
    } catch (err) {
      toast.error('Błąd połączenia');
    }
  };

  return (
    <div style={{ display: 'grid', gap: '1rem' }}>
      {/* Formularz dodawania/edycji celu */}
      <div style={{
        border: '1px solid var(--border)',
        borderRadius: '0.375rem',
        backgroundColor: 'var(--card-bg)',
        padding: '1rem'
      }}>
        <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: '600' }}>
          🎯 {editingSalesTarget ? 'Edytuj cel sprzedaży' : 'Ustaw cel sprzedaży dla lokalizacji'}
        </h3>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label style={{ fontSize: '11px', marginBottom: '3px', color: 'var(--text-muted)' }}>Lokalizacja</label>
            <select
              value={newSalesTarget.location_id || ''}
              onChange={(e) => setNewSalesTarget({ ...newSalesTarget, location_id: e.target.value })}
              style={{
                padding: '6px 10px',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                fontSize: '12px',
                minWidth: '160px',
                height: '30px',
                backgroundColor: 'var(--card-bg)',
                color: 'var(--text)'
              }}
            >
              <option value="">-- Wybierz --</option>
              {(locations || []).map(loc => (
                <option key={loc.id} value={loc.id}>{loc.name}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label style={{ fontSize: '11px', marginBottom: '3px', color: 'var(--text-muted)' }}>Rok</label>
            <select
              value={newSalesTarget.year || new Date().getFullYear()}
              onChange={(e) => setNewSalesTarget({ ...newSalesTarget, year: parseInt(e.target.value) })}
              style={{
                padding: '6px 10px',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                fontSize: '12px',
                height: '30px',
                minWidth: '70px',
                backgroundColor: 'var(--card-bg)',
                color: 'var(--text)'
              }}
            >
              {[2024, 2025, 2026, 2027].map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label style={{ fontSize: '11px', marginBottom: '3px', color: 'var(--text-muted)' }}>Miesiąc</label>
            <select
              value={newSalesTarget.month || (new Date().getMonth() + 1)}
              onChange={(e) => setNewSalesTarget({ ...newSalesTarget, month: parseInt(e.target.value) })}
              style={{
                padding: '6px 10px',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                fontSize: '12px',
                height: '30px',
                minWidth: '100px',
                backgroundColor: 'var(--card-bg)',
                color: 'var(--text)'
              }}
            >
              {['Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec', 'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'].map((m, i) => (
                <option key={i+1} value={i+1}>{m}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label style={{ fontSize: '11px', marginBottom: '3px', color: 'var(--text-muted)' }}>Kwota celu (zł)</label>
            <input
              type="number"
              value={newSalesTarget.target_amount || ''}
              onChange={(e) => setNewSalesTarget({ ...newSalesTarget, target_amount: e.target.value })}
              placeholder="np. 50000"
              style={{
                padding: '6px 10px',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                fontSize: '12px',
                width: '100px',
                height: '30px',
                backgroundColor: 'var(--card-bg)',
                color: 'var(--text)',
                boxSizing: 'border-box'
              }}
            />
          </div>
          <button
            onClick={saveSalesTarget}
            style={{
              padding: '6px 12px',
              height: '30px',
              backgroundColor: editingSalesTarget ? 'var(--info)' : 'var(--success)',
              color: '#fff' ,
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '500'
            }}
          >
            💾 {editingSalesTarget ? 'Zapisz' : 'Dodaj'}
          </button>
          {editingSalesTarget && (
            <button
              onClick={() => {
                setEditingSalesTarget(null);
                setNewSalesTarget({ location_id: '', year: new Date().getFullYear(), month: new Date().getMonth() + 1, target_amount: '' });
              }}
              style={{
                padding: '6px 12px',
                height: '30px',
                border: '1px solid var(--text-muted)', color: 'var(--text-muted)' ,
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              ✖ Anuluj
            </button>
          )}
        </div>
      </div>

      {/* Lista celów */}
      <div style={{
        border: '1px solid var(--border)',
        borderRadius: '0.375rem',
        backgroundColor: 'var(--card-bg)',
        padding: '1rem'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '10px' }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600' }}>
            📋 Historia celów sprzedaży
          </h3>

          {/* Filtry */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              value={salesTargetFilter.year || ''}
              onChange={(e) => {
                const newFilter = { ...salesTargetFilter, year: e.target.value ? parseInt(e.target.value) : null };
                setSalesTargetFilter(newFilter);
                loadSalesTargets(newFilter);
              }}
              style={{ padding: '6px 10px', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)', fontSize: '12px', height: '28px' }}
            >
              <option value="">Wszystkie lata</option>
              {[2024, 2025, 2026, 2027].map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>

            <select
              value={salesTargetFilter.month || ''}
              onChange={(e) => {
                const newFilter = { ...salesTargetFilter, month: e.target.value ? parseInt(e.target.value) : null };
                setSalesTargetFilter(newFilter);
                loadSalesTargets(newFilter);
              }}
              style={{ padding: '6px 10px', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)', fontSize: '12px', height: '28px' }}
            >
              <option value="">Wszystkie miesiące</option>
              {['Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec', 'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'].map((m, i) => (
                <option key={i+1} value={i+1}>{m}</option>
              ))}
            </select>

            <select
              value={salesTargetFilter.location_id || ''}
              onChange={(e) => {
                const newFilter = { ...salesTargetFilter, location_id: e.target.value ? parseInt(e.target.value) : null };
                setSalesTargetFilter(newFilter);
                loadSalesTargets(newFilter);
              }}
              style={{ padding: '6px 10px', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)', fontSize: '12px', height: '28px' }}
            >
              <option value="">Wszystkie lokalizacje</option>
              {(locations || []).map(loc => (
                <option key={loc.id} value={loc.id}>{loc.name}</option>
              ))}
            </select>

            <button
              onClick={() => {
                const newFilter = { year: null, month: null, location_id: null };
                setSalesTargetFilter(newFilter);
                loadSalesTargets(newFilter);
              }}
              style={{
                padding: '4px 10px',
                height: '28px',
                border: '1px solid var(--text-muted)', color: 'var(--text-muted)' ,
                cursor: 'pointer',
                fontSize: '11px'
              }}
            >
              🔄 Reset
            </button>
          </div>
        </div>

        {salesTargetsLoading ? (
          <p>Ładowanie...</p>
        ) : salesTargets.length === 0 ? (
          <p style={{ color: 'var(--text-muted)' }}>Brak zdefiniowanych celów sprzedaży dla wybranych filtrów.</p>
        ) : (
          <div className="admin-table-wrap" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--bg)' }}>
                <th className="col-hide-mobile" style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Lokalizacja</th>
                <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid var(--border)' }}>Rok</th>
                <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid var(--border)' }}>Miesiąc</th>
                <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid var(--border)' }}>Cel (zł)</th>
                <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid var(--border)' }}>Aktualna sprzedaż</th>
                <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid var(--border)' }}>Postęp</th>
                <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid var(--border)' }}>Akcje</th>
              </tr>
            </thead>
            <tbody>
              {salesTargets.map((target) => {
                const progress = target.current_revenue && target.target_amount
                  ? Math.min((target.current_revenue / target.target_amount) * 100, 100)
                  : 0;
                const monthNames = ['Sty', 'Lut', 'Mar', 'Kwi', 'Maj', 'Cze', 'Lip', 'Sie', 'Wrz', 'Paź', 'Lis', 'Gru'];
                return (
                  <tr key={target.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td className="col-hide-mobile" style={{ padding: '10px' }}>{target.location_name || `Lokalizacja #${target.location_id}`}</td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>{target.year}</td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>{monthNames[target.month - 1]}</td>
                    <td style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold' }}>
                      {parseFloat(target.target_amount || 0).toLocaleString()} zł
                    </td>
                    <td style={{ padding: '10px', textAlign: 'right', color: 'var(--success)' }}>
                      {parseFloat(target.current_revenue || 0).toLocaleString()} zł
                    </td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{
                          flex: 1,
                          height: '8px',
                          backgroundColor: 'var(--border)',
                          borderRadius: '4px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${progress}%`,
                            height: '100%',
                            backgroundColor: progress >= 100 ? 'var(--success)' : 'var(--warning)',
                            borderRadius: '4px',
                            transition: 'width 0.3s'
                          }} />
                        </div>
                        <span style={{
                          fontSize: '12px',
                          fontWeight: 'bold',
                          color: progress >= 100 ? 'var(--success)' : 'var(--warning)'
                        }}>
                          {progress.toFixed(0)}%
                        </span>
                      </div>
                    </td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                        <button
                          onClick={() => {
                            setEditingSalesTarget(target);
                            setNewSalesTarget({
                              id: target.id,
                              location_id: target.location_id,
                              year: target.year,
                              month: target.month,
                              target_amount: target.target_amount
                            });
                          }}
                          style={{
                            padding: '4px 10px',
                            border: '1px solid var(--info)', color: 'var(--info)' ,
                            cursor: 'pointer',
                            fontSize: '11px'
                          }}
                        >
                          ✏️ Edytuj
                        </button>
                        <button
                          onClick={() => deleteSalesTarget(target.id)}
                          style={{
                            padding: '4px 10px',
                            border: '1px solid var(--danger)', color: 'var(--danger)' ,
                            cursor: 'pointer',
                            fontSize: '11px'
                          }}
                        >
                          🗑️ Usuń
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesTargetsTab;
